import React, { useState, useEffect, useRef } from 'react';
import { supabase, storageHelpers } from '../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, 
  Save, 
  Camera, 
  User, 
  Mail, 
  Calendar,
  Edit,
  Loader2,
  CheckCircle,
  X,
  Bookmark,
  Zap,
  ShoppingBag
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import SavedTopicsPage from './SavedTopicsPage';
import { StoreIntegrationsTab } from './settings/StoreIntegrationsTab';
import { SellerRevenueProgressBar } from './gamification/SellerRevenueProgressBar';

interface ProfilePageProps {
  onBack?: () => void;
  onNavigateToCommunity?: (topicId: string, categoryId: string) => void;
  initialTab?: 'profile' | 'integrations';
}

const ProfilePage: React.FC<ProfilePageProps> = ({ onBack, onNavigateToCommunity, initialTab = 'profile' }) => {
  const { refreshProfile } = useAuth();
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'integrations'>(initialTab);
  const [showSavedTopics, setShowSavedTopics] = useState(false);
  const [avatarKey, setAvatarKey] = useState(0); // Para forçar re-render do avatar
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<{
    full_name: string;
    bio: string;
    website: string;
    location: string;
  }>({
    full_name: '',
    bio: '',
    website: '',
    location: ''
  });

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        // Se não há usuário logado, não podemos mostrar o perfil
        return;
      }

      setUser(currentUser);

      // Carregar perfil do usuário
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', currentUser.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Erro ao carregar perfil:', profileError);
      } else if (profileData) {
        setProfile(profileData);
        setFormData({
          full_name: (profileData.full_name as string) || '',
          bio: (profileData.bio as string) || '',
          website: (profileData.website as string) || '',
          location: (profileData.location as string) || ''
        });
      }
    } catch (err) {
      console.error('Erro inesperado:', err);
      setError('Erro ao carregar dados do usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (file: File) => {
    if (!user) return;

    try {
      setUploadingAvatar(true);
      setError(null);
      
      console.log('Iniciando upload de avatar para usuário:', user.id);

      const { url, error } = await storageHelpers.uploadProfileImage(file, user.id);
      
      if (error) {
        console.error('Erro no upload:', error);
        setError('Erro ao fazer upload da imagem: ' + error);
        return;
      }

      console.log('Upload bem-sucedido, URL:', url);

      // Atualizar perfil com nova URL do avatar
      const { error: updateError } = await supabase
        .from('user_profiles')
        .upsert({
          id: user.id,
          email: user.email, // Adicionando o email obrigatório
          avatar_url: url,
          updated_at: new Date().toISOString()
        });

      if (updateError) {
        console.error('Erro ao atualizar perfil:', updateError);
        setError('Erro ao atualizar avatar: ' + updateError.message);
        return;
      }

      console.log('Perfil atualizado com sucesso');

      // Atualizar estado local com timestamp para forçar reload da imagem
      const newAvatarUrl = `${url}?t=${Date.now()}`;
      setProfile(prev => ({ ...prev, avatar_url: newAvatarUrl }));
      setAvatarKey(prev => prev + 1); // Força re-render do avatar
      
      // Forçar atualização do contexto de autenticação
      await refreshProfile();
      
      setSuccess('Foto de perfil atualizada com sucesso!');
      
      // Limpar mensagem de sucesso após 3 segundos
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Erro inesperado no upload:', err);
      setError('Erro inesperado no upload da imagem');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarClick = () => {
    // Limpar o input antes de abrir o seletor de arquivo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tipo de arquivo
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Por favor, selecione uma imagem válida (JPEG, PNG, GIF ou WebP)');
        return;
      }
      
      // Validar tamanho do arquivo (máximo 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setError('A imagem deve ter no máximo 5MB');
        return;
      }
      
      handleAvatarUpload(file);
    }
    // Limpar o input após processar o arquivo
    if (e.target) {
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      // Remover campos que não existem na tabela para evitar erro
      const updateData: any = {
        id: user.id,
        email: user.email, // Adicionando o email obrigatório
        full_name: formData.full_name.trim(),
        updated_at: new Date().toISOString()
      };

      // Adicionar campos opcionais apenas se existirem na tabela
      // (comentados até confirmar se existem no banco)
      // if (formData.bio.trim()) updateData.bio = formData.bio.trim();
      // if (formData.website.trim()) updateData.website = formData.website.trim();
      // if (formData.location.trim()) updateData.location = formData.location.trim();

      const { error } = await supabase
        .from('user_profiles')
        .upsert(updateData);

      if (error) {
        setError('Erro ao salvar perfil: ' + error.message);
      } else {
        setSuccess('Perfil atualizado com sucesso!');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Erro inesperado:', err);
      setError('Erro inesperado ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev: {
      full_name: string;
      bio: string;
      website: string;
      location: string;
    }) => ({
      ...prev,
      [field]: value
    }));
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Se estiver mostrando a página de salvos
  if (showSavedTopics) {
    return (
      <SavedTopicsPage 
        onBack={() => setShowSavedTopics(false)}
        onNavigateToTopic={(topicId, categoryId) => {
          if (onNavigateToCommunity) {
            onNavigateToCommunity(topicId, categoryId);
          }
          setShowSavedTopics(false);
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#121316] via-[#1a1d23] to-[#121316] p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-700 rounded-lg mb-6 w-32"></div>
            <div className="h-64 bg-gray-700 rounded-xl mb-6"></div>
            <div className="h-32 bg-gray-700 rounded-xl mb-6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#121316] via-[#1a1d23] to-[#121316] p-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-[#1a1d23]/50 backdrop-blur-sm rounded-3xl border border-gray-700/50 p-12">
            <User className="w-16 h-16 text-gray-400 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-4">Acesso Restrito</h2>
            <p className="text-gray-400 mb-6">Faça login para acessar seu perfil.</p>
            {onBack && (
              <Button onClick={onBack} className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#121316] via-[#1a1d23] to-[#121316]">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Header & Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          {onBack && (
            <Button 
              onClick={onBack}
              variant="ghost" 
              className="group hover:bg-white/10 transition-all duration-200 text-gray-300 hover:text-white self-start"
            >
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
              Voltar
            </Button>
          )}

          <div className="flex items-center gap-2 bg-[#1a1d23]/80 p-1.5 rounded-2xl border border-gray-700/60 self-start sm:self-auto overflow-x-auto max-w-full">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                activeTab === 'profile'
                  ? 'bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] shadow-md shadow-[#0FFF55]/20'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
              }`}
            >
              <User className="w-4 h-4" />
              Meu Perfil
            </button>
            <button
              onClick={() => setActiveTab('integrations')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${
                activeTab === 'integrations'
                  ? 'bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] shadow-md shadow-[#0FFF55]/20'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
              }`}
            >
              <Zap className="w-4 h-4 text-yellow-400" />
              Integrações
              <span className="bg-yellow-400/20 text-yellow-300 text-[10px] font-black px-1.5 py-0.5 rounded-md border border-yellow-400/40">
                Master Shop
              </span>
            </button>
            <button
              onClick={() => setShowSavedTopics(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-gray-400 hover:text-white hover:bg-gray-800/50 transition-all whitespace-nowrap"
            >
              <Bookmark className="w-4 h-4" />
              Salvos
            </button>
          </div>
        </div>

        {/* Conteúdo da Aba de Integrações */}
        {activeTab === 'integrations' ? (
          <StoreIntegrationsTab userId={user.id} />
        ) : (
          <>
            {/* Régua de Faturamento no Perfil */}
            <SellerRevenueProgressBar 
              userId={user.id} 
              onOpenIntegrations={() => setActiveTab('integrations')} 
            />

            {/* Perfil Principal */}
            <div className="bg-[#1a1d23]/50 backdrop-blur-sm rounded-3xl border border-gray-700/50 p-8 shadow-2xl mb-8">
              <div className="flex items-start gap-8 mb-8">
                {/* Avatar Section */}
                <div className="flex flex-col items-center gap-4">
                  <div className="relative group">
                    <Avatar className="w-32 h-32 ring-4 ring-[#0FFF55]/50 shadow-2xl cursor-pointer transition-all duration-200 group-hover:ring-[#0FFF55]/70" key={avatarKey}>
                      <AvatarImage 
                        src={profile?.avatar_url} 
                        key={`${profile?.avatar_url}-${avatarKey}`} // Força re-render quando a URL muda
                      />
                      <AvatarFallback className="bg-gradient-to-br from-[#0FFF55] to-[#0dd149] text-[#121316] text-3xl font-bold">
                        {getInitials(profile?.full_name || user?.email || 'U')}
                      </AvatarFallback>
                    </Avatar>

                
                {/* Overlay de upload */}
                <div 
                  className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer"
                  onClick={handleAvatarClick}
                  title="Clique para alterar a foto"
                >
                  {uploadingAvatar ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : (
                    <div className="flex flex-col items-center gap-1">
                      <Camera className="w-6 h-6 text-white" />
                      <span className="text-xs text-white font-medium">Alterar</span>
                    </div>
                  )}
                </div>
              </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                />
              
              <Button 
                onClick={handleAvatarClick}
                variant="outline"
                size="sm"
                disabled={uploadingAvatar}
                className="border-gray-600 text-gray-300 hover:bg-gray-700/50"
              >
                {uploadingAvatar ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                {uploadingAvatar ? 'Enviando...' : 'Alterar Foto'}
              </Button>
            </div>

            {/* Info Section */}
            <div className="flex-1">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-white mb-2">
                  {profile?.full_name || user?.email}
                </h1>
                <p className="text-gray-400 text-lg">
                  {user?.email}
                </p>
                {profile?.role === 'admin' && (
                  <Badge className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] border-0 px-3 py-1 mt-3">
                    <User className="w-3 h-3 mr-1" />
                    Administrador
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-3 text-gray-400">
                  <Calendar className="w-4 h-4" />
                  <span>Membro desde {formatDistanceToNow(new Date(user?.created_at), {
                    addSuffix: true,
                    locale: ptBR
                  })}</span>
                </div>
                
                {profile?.location && (
                  <div className="flex items-center gap-3 text-gray-400">
                    <User className="w-4 h-4" />
                    <span>{profile.location}</span>
                  </div>
                )}
                
                {profile?.website && (
                  <div className="flex items-center gap-3 text-gray-400">
                    <Mail className="w-4 h-4" />
                    <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-[#0FFF55] hover:text-[#0dd149]">
                      {profile.website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>

          {profile?.bio && (
            <div className="border-t border-gray-700/50 pt-6">
              <h3 className="text-lg font-semibold text-white mb-3">Sobre</h3>
              <p className="text-gray-300 leading-relaxed">{profile.bio}</p>
            </div>
          )}
        </div>

        {/* Ações Rápidas */}
        <div className="bg-[#1a1d23]/50 backdrop-blur-sm rounded-3xl border border-gray-700/50 p-8 shadow-2xl mb-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">
              Ações Rápidas
            </h2>
            <p className="text-gray-400">
              Acesse rapidamente suas funcionalidades
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              onClick={() => setActiveTab('integrations')}
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#0FFF55]/10 to-[#0FFF55]/5 border border-[#0FFF55]/20 rounded-xl hover:bg-gradient-to-r hover:from-[#0FFF55]/20 hover:to-[#0FFF55]/10 transition-all duration-200 group"
            >
              <div className="w-10 h-10 bg-[#0FFF55]/20 rounded-lg flex items-center justify-center group-hover:bg-[#0FFF55]/30 transition-colors">
                <Zap className="w-5 h-5 text-[#0FFF55]" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-white">Minhas Lojas Master Shop</h3>
                <p className="text-sm text-gray-400">Adicionar, editar e sincronizar chaves</p>
              </div>
            </button>

            <button
              onClick={() => setShowSavedTopics(true)}
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#0FFF55]/10 to-[#0FFF55]/5 border border-[#0FFF55]/20 rounded-xl hover:bg-gradient-to-r hover:from-[#0FFF55]/20 hover:to-[#0FFF55]/10 transition-all duration-200 group"
            >
              <div className="w-10 h-10 bg-[#0FFF55]/20 rounded-lg flex items-center justify-center group-hover:bg-[#0FFF55]/30 transition-colors">
                <Bookmark className="w-5 h-5 text-[#0FFF55]" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-white">Tópicos Salvos</h3>
                <p className="text-sm text-gray-400">Suas discussões favoritas</p>
              </div>
            </button>
          </div>
        </div>

        {/* Formulário de Edição */}
        <div className="bg-[#1a1d23]/50 backdrop-blur-sm rounded-3xl border border-gray-700/50 p-8 shadow-2xl">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-3">
              Editar Perfil
            </h2>
            <p className="text-gray-400">
              Atualize suas informações pessoais
            </p>
            <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-500/50 rounded-lg">
              <p className="text-yellow-400 text-sm">
                ⚠️ Nota: Apenas o nome completo pode ser editado no momento. Os outros campos serão habilitados em breve.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                <Label htmlFor="full_name" className="text-white font-semibold mb-3 block">
                  Nome Completo
                </Label>
                <Input
                  id="full_name"
                    type="text"
                    value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  placeholder="Seu nome completo"
                  className="bg-gray-700/30 border-gray-600/50 text-white placeholder-gray-500 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#0FFF55]/50 focus:border-[#0FFF55]/50 transition-all duration-200"
                  />
                </div>

                <div>
                <Label htmlFor="location" className="text-white font-semibold mb-3 block">
                  Localização
                </Label>
                <Input
                  id="location"
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="Sua cidade/país"
                  disabled
                  className="bg-gray-700/30 border-gray-600/50 text-gray-500 placeholder-gray-500 rounded-xl px-4 py-3 transition-all duration-200 cursor-not-allowed"
                    />
                  </div>
            </div>

                  <div>
              <Label htmlFor="website" className="text-white font-semibold mb-3 block">
                Website
              </Label>
              <Input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                placeholder="https://seu-site.com"
                disabled
                className="bg-gray-700/30 border-gray-600/50 text-gray-500 placeholder-gray-500 rounded-xl px-4 py-3 transition-all duration-200 cursor-not-allowed"
              />
                  </div>

                  <div>
              <Label htmlFor="bio" className="text-white font-semibold mb-3 block">
                Biografia
              </Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                placeholder="Conte um pouco sobre você..."
                rows={4}
                disabled
                className="bg-gray-700/30 border-gray-600/50 text-gray-500 placeholder-gray-500 rounded-xl px-4 py-3 transition-all duration-200 resize-none cursor-not-allowed"
              />
                  </div>

            <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-700/50">
              <Button 
                      type="submit"
                disabled={saving}
                className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] hover:from-[#0dd149] hover:to-[#0FFF55] text-[#121316] px-8 py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-semibold"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Save className="w-5 h-5 mr-2" />
                )}
                {saving ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
                  </div>
                </form>

          {error && (
            <div className="mt-6 p-4 bg-red-900/20 border border-red-500/50 rounded-xl">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {success && (
            <div className="mt-6 p-4 bg-green-900/20 border border-green-500/50 rounded-xl">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <p className="text-green-400">{success}</p>
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;